"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEventWatchingOwner = exports.getGitHubEventSubItems = exports.mergeEventPreservingEnhancement = exports.mergeEventsPreservingEnhancement = exports.getEventIconAndColor = exports.sortEvents = exports.isTagMainEvent = exports.isBranchMainEvent = exports.mergeSimilarEvents = exports.getEventMetadata = exports.getEventActionMetadata = exports.getOlderOrNewerEventDate = exports.eventSubjectTypes = exports.eventActions = void 0;
const immer_1 = __importDefault(require("immer"));
const lodash_1 = __importDefault(require("lodash"));
const moment_1 = __importDefault(require("moment"));
const utils_1 = require("../../utils");
const shared_1 = require("../shared");
const shared_2 = require("./shared");
const url_1 = require("./url");
exports.eventActions = [
    'added',
    'closed',
    'commented',
    'created',
    'deleted',
    'forked',
    'merged',
    'opened',
    'pushed',
    'released',
    'reopened',
    'reviewed',
    'starred',
    'updated',
];
exports.eventSubjectTypes = [
    'Branch',
    'Commit',
    'Issue',
    'PullRequest',
    'PullRequestReview',
    'Release',
    'Repository',
    'Tag',
    'User',
    'Wiki',
];
function getOlderOrNewerEventDate(order, items, { ignoreFutureDates = true } = {}) {
    const now = Date.now();
    return sortEvents(items, 'created_at', order === 'newer' ? 'desc' : 'asc')
        .map(item => item.created_at)
        .filter(date => !!(date && ignoreFutureDates ? now > new Date(date).getTime() : true))[0];
}
exports.getOlderOrNewerEventDate = getOlderOrNewerEventDate;
function getEventActionMetadata(action) {
    return { label: lodash_1.default.capitalize(lodash_1.default.startCase(action)), action };
}
exports.getEventActionMetadata = getEventActionMetadata;
function getEventMetadata(event, options = {}) {
    const { appendColon, commitIsKnown, includeBranch, includeFork, includeRepo, includeTag, includeUser, issueOrPullRequestIsKnown, ownerIsKnown, repoIsKnown, } = options;
    const repoFullName = ('repo' in event && url_1.getRepoFullNameFromObject(event.repo)) ||
        ('repos' in event && url_1.getRepoFullNameFromObject(event.repos[0]));
    const isDraftPR = event &&
        event.payload &&
        (('pull_request' in event.payload && shared_2.isDraft(event.payload.pull_request)) ||
            ('issue' in event.payload &&
                shared_2.isPullRequest(event.payload.issue) &&
                shared_2.isDraft(event.payload.issue)));
    const issueText = issueOrPullRequestIsKnown ? 'this issue' : 'an issue';
    const pullRequestText = issueOrPullRequestIsKnown
        ? `this ${isDraftPR ? 'draft pr' : 'pr'}`
        : `a ${isDraftPR ? 'draft pr' : 'pr'}`;
    let repositoryText = repoIsKnown
        ? 'this repository'
        : (includeRepo && repoFullName) || 'a repository';
    if (ownerIsKnown && repositoryText === repoFullName) {
        const { owner } = shared_2.getOwnerAndRepo(repositoryText);
        if (owner)
            repositoryText = repositoryText.replace(`${owner}/`, '');
    }
    const colonText = appendColon ? ':' : '';
    const issueTextWithColon = issueOrPullRequestIsKnown
        ? issueText
        : `${issueText}${colonText}`;
    const pullRequestTextWithColon = issueOrPullRequestIsKnown
        ? pullRequestText
        : `${pullRequestText}${colonText}`;
    const repositoryTextWithColon = repoIsKnown
        ? repositoryText
        : `${repositoryText}${colonText}`;
    const result = (() => {
        switch (event.type) {
            case 'CommitCommentEvent': {
                return {
                    action: 'commented',
                    actionText: `Commented on commit${colonText}`,
                    subjectType: 'Commit',
                };
            }
            case 'CreateEvent': {
                switch (event.payload.ref_type) {
                    case 'repository':
                        return {
                            action: 'created',
                            actionText: `Created ${repositoryTextWithColon}`,
                            subjectType: 'Repository',
                        };
                    case 'branch': {
                        const branch = shared_2.getNameFromRef(event.payload.ref || undefined);
                        return {
                            action: 'created',
                            actionText: includeBranch && branch
                                ? `Created branch ${branch}`
                                : `Created a branch${colonText}`,
                            subjectType: 'Branch',
                        };
                    }
                    case 'tag': {
                        const tag = event.payload.ref;
                        return {
                            action: 'created',
                            actionText: includeTag && tag
                                ? `Created tag ${tag}`
                                : `Created a tag${colonText}`,
                            subjectType: 'Tag',
                        };
                    }
                    default:
                        return {
                            action: 'created',
                            actionText: 'Created something',
                            subjectType: undefined,
                        };
                }
            }
            case 'DeleteEvent': {
                switch (event.payload.ref_type) {
                    case 'repository':
                        return {
                            action: 'deleted',
                            actionText: `Deleted ${repositoryTextWithColon}`,
                            subjectType: 'Repository',
                        };
                    case 'branch': {
                        const branch = shared_2.getNameFromRef(event.payload.ref);
                        return {
                            action: 'deleted',
                            actionText: includeBranch && branch
                                ? `Deleted branch ${branch}`
                                : `Deleted a branch${colonText}`,
                            subjectType: 'Branch',
                        };
                    }
                    case 'tag': {
                        const tag = event.payload.ref;
                        return {
                            action: 'deleted',
                            actionText: includeTag && tag
                                ? `Deleted tag ${tag}`
                                : `Deleted a tag${colonText}`,
                            subjectType: 'Tag',
                        };
                    }
                    default:
                        return {
                            action: 'deleted',
                            actionText: 'Deleted something',
                            subjectType: undefined,
                        };
                }
            }
            case 'ForkEvent': {
                const fork = event.payload.forkee;
                const forkFullName = fork && (fork.full_name || url_1.getRepoFullNameFromObject(fork));
                const isSameName = forkFullName &&
                    forkFullName.split('/')[1] === repositoryText.split('/')[1];
                return {
                    action: 'forked',
                    actionText: includeFork
                        ? `Forked ${repositoryText}${!repoIsKnown && forkFullName && !isSameName
                            ? ` to ${forkFullName}`
                            : ''}`
                        : `Forked ${repositoryTextWithColon}`,
                    subjectType: 'Repository',
                };
            }
            case 'GollumEvent': {
                const count = (event.payload.pages || []).length || 1;
                const pagesText = count > 1 ? `${count} wiki pages` : 'a wiki page';
                switch (event.payload.pages &&
                    event.payload.pages[0] &&
                    event.payload.pages[0].action) {
                    case 'created': {
                        return {
                            action: 'created',
                            actionText: `Created ${pagesText}${colonText}`,
                            subjectType: 'Wiki',
                        };
                    }
                    case 'edited': {
                        return {
                            action: 'updated',
                            actionText: `Edited ${pagesText}${colonText}`,
                            subjectType: 'Wiki',
                        };
                    }
                    default:
                        return {
                            action: 'updated',
                            actionText: `Updated ${pagesText}${colonText}`,
                            subjectType: 'Wiki',
                        };
                }
            }
            case 'IssueCommentEvent': {
                return {
                    action: 'commented',
                    actionText: `Commented on ${shared_2.isPullRequest(event.payload.issue)
                        ? pullRequestTextWithColon
                        : issueTextWithColon}`,
                    subjectType: shared_2.isPullRequest(event.payload.issue)
                        ? 'PullRequest'
                        : 'Issue',
                };
            }
            case 'IssuesEvent': {
                const subjectType = shared_2.isPullRequest(event.payload.issue)
                    ? 'PullRequest'
                    : 'Issue';
                switch (event.payload.action) {
                    case 'closed':
                        return {
                            action: 'closed',
                            actionText: `Closed ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'reopened':
                        return {
                            action: 'reopened',
                            actionText: `Reopened ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'opened':
                        return {
                            action: 'opened',
                            actionText: `Opened ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'assigned':
                        return {
                            action: 'updated',
                            actionText: `Assigned ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'unassigned':
                        return {
                            action: 'updated',
                            actionText: `Unassigned ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'labeled':
                        return {
                            action: 'updated',
                            actionText: `Labeled ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'unlabeled':
                        return {
                            action: 'updated',
                            actionText: `Unlabeled ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'edited':
                        return {
                            action: 'updated',
                            actionText: `Edited ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'milestoned':
                        return {
                            action: 'updated',
                            actionText: `Milestoned ${issueTextWithColon}`,
                            subjectType,
                        };
                    case 'demilestoned':
                        return {
                            action: 'updated',
                            actionText: `Demilestoned ${issueTextWithColon}`,
                            subjectType,
                        };
                    default: {
                        console.error('Unknown IssuesEvent action', event.payload.action);
                        return {
                            action: 'updated',
                            actionText: `Updated ${issueTextWithColon}`,
                            subjectType,
                        };
                    }
                }
            }
            case 'MemberEvent': {
                const username = event.payload.member && event.payload.member.login;
                return {
                    action: 'added',
                    actionText: includeUser && username
                        ? `Added @${username} to ${repositoryTextWithColon}`
                        : `Added a user to ${repositoryTextWithColon}`,
                    subjectType: 'User',
                };
            }
            case 'PublicEvent': {
                return {
                    action: 'created',
                    actionText: `Made ${repositoryText} public`,
                    subjectType: 'Repository',
                };
            }
            case 'PullRequestEvent': {
                const subjectType = 'PullRequest';
                switch (event.payload.action) {
                    case 'assigned':
                        return {
                            action: 'updated',
                            actionText: `Assigned ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    case 'unassigned':
                        return {
                            action: 'updated',
                            actionText: `Unassigned ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    case 'labeled':
                        return {
                            action: 'updated',
                            actionText: `Labeled ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    case 'unlabeled':
                        return {
                            action: 'updated',
                            actionText: `Unlabeled ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    case 'opened':
                        return {
                            action: 'opened',
                            actionText: `Opened ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    case 'edited':
                        return {
                            action: 'updated',
                            actionText: `Edited ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    case 'closed': {
                        const isMerged = !!(event.payload.pull_request.merged ||
                            event.payload.pull_request.merged_at);
                        return {
                            action: isMerged ? 'merged' : 'closed',
                            actionText: isMerged
                                ? `Merged ${pullRequestTextWithColon}`
                                : `Closed ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    }
                    case 'reopened':
                        return {
                            action: 'reopened',
                            actionText: `Reopened ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    default: {
                        console.error('Unknown PullRequestEvent action', event.payload.action);
                        return {
                            action: 'updated',
                            actionText: `Updated ${pullRequestTextWithColon}`,
                            subjectType,
                        };
                    }
                }
            }
            case 'PullRequestReviewEvent': {
                return {
                    action: 'reviewed',
                    actionText: `Reviewed ${pullRequestTextWithColon}`,
                    subjectType: 'PullRequestReview',
                };
            }
            case 'PullRequestReviewCommentEvent': {
                switch (event.payload.action) {
                    case 'created':
                        return {
                            action: 'reviewed',
                            actionText: `Commented on ${pullRequestText} review${colonText}`,
                            subjectType: 'PullRequestReview',
                        };
                    case 'edited':
                        return {
                            action: 'updated',
                            actionText: `Edited ${pullRequestText} review${colonText}`,
                            subjectType: 'PullRequestReview',
                        };
                    case 'deleted':
                        return {
                            action: 'deleted',
                            actionText: `Deleted ${pullRequestText} review${colonText}`,
                            subjectType: 'PullRequestReview',
                        };
                    default: {
                        console.error('Unknown PullRequestReviewCommentEvent action', event.payload.action);
                        return {
                            action: 'reviewed',
                            actionText: `Updated ${pullRequestText} review${colonText}`,
                            subjectType: 'PullRequestReview',
                        };
                    }
                }
            }
            case 'PushEvent': {
                const commits = event.payload.commits || [{}];
                const count = Math.max(commits.length || 1, event.payload.distinct_size || 1, event.payload.size || 1);
                const branch = shared_2.getNameFromRef(event.payload.ref);
                const pushedText = event.forced ? 'Force pushed' : 'Pushed';
                const commitText = count > 1
                    ? ` ${count} commits`
                    : commitIsKnown
                        ? ' this commit'
                        : ' a commit';
                const branchText = includeBranch && branch ? ` to ${branch}` : '';
                return {
                    action: 'pushed',
                    actionText: `${pushedText}${commitText}${branchText}${colonText}`,
                    subjectType: 'Commit',
                };
            }
            case 'ReleaseEvent': {
                return {
                    action: 'released',
                    actionText: `Published a release${colonText}`,
                    subjectType: 'Release',
                };
            }
            case 'WatchEvent': {
                return {
                    action: 'starred',
                    actionText: `Starred ${repositoryTextWithColon}`,
                    subjectType: 'Repository',
                };
            }
            case 'WatchEvent:OneUserMultipleRepos': {
                return {
                    action: 'starred',
                    actionText: event.repos.length > 1
                        ? `Starred ${event.repos.length} repositories${colonText}`
                        : `Starred ${repositoryTextWithColon}`,
                    subjectType: 'Repository',
                };
            }
            default: {
                console.error('Unknown event type', event.type);
                return {
                    action: undefined,
                    actionText: 'Did something',
                    subjectType: undefined,
                };
            }
        }
    })();
    return {
        ...result,
        actionText: result.actionText.replace(/ {2}/g, ' ').trim(),
    };
}
exports.getEventMetadata = getEventMetadata;
function tryMerge(eventA, eventB, maxLength) {
    if (!eventA || !eventB)
        return null;
    const isSameUser = eventA.actor && eventB.actor && eventA.actor.id === eventB.actor.id;
    const isSameRepo = 'repo' in eventA &&
        eventA.repo &&
        ('repo' in eventB && eventB.repo && eventA.repo.id === eventB.repo.id);
    const createdAtMinutesDiff = moment_1.default(eventA.created_at).diff(moment_1.default(eventB.created_at), 'minutes');
    // only merge events from the same user
    if (!isSameUser)
        return null;
    // only merge events that were created in the same hour
    if (createdAtMinutesDiff >= 24 * 60)
        return null;
    // only merge 5 events at max
    if ('merged' in eventA && eventA.merged && eventA.merged.length >= maxLength)
        return null;
    switch (eventA.type) {
        case 'WatchEvent': {
            if (eventB.type === 'WatchEvent') {
                if (isSameRepo)
                    return eventB;
                return {
                    ...lodash_1.default.omit(eventA, ['repo', 'type']),
                    type: 'WatchEvent:OneUserMultipleRepos',
                    repos: [eventA.repo, eventB.repo],
                    merged: [eventA.id, eventB.id],
                };
            }
            return null;
        }
        case 'WatchEvent:OneUserMultipleRepos': {
            const repoBId = 'repo' in eventB && eventB.repo && eventB.repo.id;
            const alreadyMergedThisRepo = eventA.repos.find(repo => repo.id === repoBId);
            if (eventB.type === 'WatchEvent') {
                return {
                    ...eventA,
                    repos: lodash_1.default.uniqBy([...eventA.repos, eventB.repo], repo => repo.id),
                    merged: alreadyMergedThisRepo
                        ? lodash_1.default.uniq(eventA.merged)
                        : lodash_1.default.uniq([...eventA.merged, eventB.id]),
                };
            }
            return null;
        }
        default:
            return null;
    }
}
function mergeSimilarEvents(events, maxLength) {
    const enhancedEvents = [];
    let enhancedEvent = null;
    events.filter(Boolean).forEach(event => {
        if (!enhancedEvent) {
            enhancedEvent = event;
            return;
        }
        const mergedEvent = tryMerge(enhancedEvent, event, maxLength);
        if (!mergedEvent) {
            enhancedEvents.push(enhancedEvent);
            enhancedEvent = event;
            return;
        }
        enhancedEvent = mergedEvent;
    });
    if (enhancedEvent)
        enhancedEvents.push(enhancedEvent);
    const result = enhancedEvents.length === events.length ? events : enhancedEvents;
    return result && result.length ? result : utils_1.constants.EMPTY_ARRAY;
}
exports.mergeSimilarEvents = mergeSimilarEvents;
function isBranchMainEvent(event) {
    if (!(event && event.type))
        return false;
    return (getEventMetadata(event).subjectType === 'Branch' ||
        event.type === 'PushEvent');
}
exports.isBranchMainEvent = isBranchMainEvent;
function isTagMainEvent(event) {
    if (!(event && event.type))
        return false;
    return getEventMetadata(event).subjectType === 'Tag';
}
exports.isTagMainEvent = isTagMainEvent;
function sortEvents(events, field = 'created_at', order = 'desc') {
    if (!(events && events.length))
        return utils_1.constants.EMPTY_ARRAY;
    return lodash_1.default(events)
        .uniqBy('id')
        .orderBy(field, order)
        .value();
}
exports.sortEvents = sortEvents;
function getEventIconAndColor(event) {
    switch (event.type) {
        case 'CommitCommentEvent':
            return {
                ...shared_2.getCommitIconAndColor(),
                subIcon: { family: 'octicon', name: 'comment-discussion' },
            };
        case 'CreateEvent': {
            switch (event.payload.ref_type) {
                case 'repository':
                    return { family: 'octicon', name: 'repo' };
                case 'branch':
                    return { family: 'octicon', name: 'git-branch' };
                case 'tag':
                    return { family: 'octicon', name: 'tag' };
                default:
                    return { family: 'octicon', name: 'plus' };
            }
        }
        case 'DeleteEvent': {
            switch (event.payload.ref_type) {
                case 'repository':
                    return {
                        family: 'octicon',
                        name: 'repo',
                        color: 'lightRed',
                    };
                case 'branch':
                    return {
                        family: 'octicon',
                        name: 'git-branch',
                        color: 'lightRed',
                    };
                case 'tag':
                    return { family: 'octicon', name: 'tag', color: 'lightRed' };
                default:
                    return { family: 'octicon', name: 'trashcan' };
            }
        }
        case 'ForkEvent':
            return { family: 'octicon', name: 'repo-forked' };
        case 'GollumEvent':
            return { family: 'octicon', name: 'book' };
        case 'IssueCommentEvent': {
            return {
                ...(shared_2.isPullRequest(event.payload.issue)
                    ? shared_2.getPullRequestIconAndColor(event.payload.issue)
                    : shared_2.getIssueIconAndColor(event.payload.issue)),
                subIcon: { family: 'octicon', name: 'comment-discussion' },
            };
        }
        case 'IssuesEvent': {
            const issue = event.payload.issue;
            switch (event.payload.action) {
                case 'opened':
                    return shared_2.getIssueIconAndColor({ state: 'open' });
                case 'closed':
                    return shared_2.getIssueIconAndColor({ state: 'closed' });
                case 'reopened':
                    return {
                        ...shared_2.getIssueIconAndColor({ state: 'open' }),
                        family: 'octicon',
                        name: 'issue-reopened',
                    };
                // case 'assigned':
                // case 'unassigned':
                // case 'labeled':
                // case 'unlabeled':
                // case 'edited':
                // case 'milestoned':
                // case 'demilestoned':
                default:
                    return shared_2.getIssueIconAndColor(issue);
            }
        }
        case 'MemberEvent':
            return { family: 'octicon', name: 'person' };
        case 'PublicEvent':
            return { family: 'octicon', name: 'globe', color: 'blue' };
        case 'PullRequestEvent': {
            const pullRequest = event.payload.pull_request;
            switch (event.payload.action) {
                case 'opened':
                case 'reopened':
                    return shared_2.getPullRequestIconAndColor({
                        draft: pullRequest.draft,
                        state: 'open',
                        merged: false,
                        merged_at: undefined,
                        mergeable_state: pullRequest.mergeable_state,
                    });
                // case 'closed': return getPullRequestIconAndColor({ state: 'closed' } as GitHubPullRequest);
                // case 'assigned':
                // case 'unassigned':
                // case 'labeled':
                // case 'unlabeled':
                // case 'edited':
                default:
                    return shared_2.getPullRequestIconAndColor(pullRequest);
            }
        }
        case 'PullRequestReviewCommentEvent':
        case 'PullRequestReviewEvent': {
            return {
                ...shared_2.getPullRequestIconAndColor(event.payload.pull_request),
                subIcon: { family: 'octicon', name: 'comment-discussion' },
            };
        }
        case 'PushEvent':
            return { family: 'octicon', name: 'code' };
        case 'ReleaseEvent':
            return isTagMainEvent(event)
                ? shared_2.getTagIconAndColor()
                : shared_2.getReleaseIconAndColor();
        case 'WatchEvent':
        case 'WatchEvent:OneUserMultipleRepos':
            return { family: 'octicon', name: 'star', color: 'yellow' };
        default: {
            const message = `Unknown event type: ${event.type}`;
            console.error(message);
            return { family: 'octicon', name: 'mark-github' };
        }
    }
}
exports.getEventIconAndColor = getEventIconAndColor;
function mergeEventsPreservingEnhancement(newItems, prevItems, { dropPrevItems } = {}) {
    const allItems = dropPrevItems
        ? newItems || []
        : lodash_1.default.concat(newItems || [], prevItems || []);
    return sortEvents(lodash_1.default.uniqBy(allItems, 'id').map(item => {
        const newItem = newItems.find(i => i.id === item.id);
        const existingItem = prevItems.find(i => i.id === item.id);
        return mergeEventPreservingEnhancement(newItem, existingItem);
    }));
}
exports.mergeEventsPreservingEnhancement = mergeEventsPreservingEnhancement;
function mergeEventPreservingEnhancement(newItem, existingItem) {
    if (!(newItem && existingItem))
        return newItem || existingItem;
    const enhancements = {
        enhanced: existingItem.enhanced,
        last_read_at: lodash_1.default.max([existingItem.last_read_at, newItem.last_read_at]),
        last_saved_at: lodash_1.default.max([existingItem.last_saved_at, newItem.last_saved_at]),
        last_unread_at: lodash_1.default.max([
            existingItem.last_unread_at,
            newItem.last_unread_at,
        ]),
        last_unsaved_at: lodash_1.default.max([
            existingItem.last_unsaved_at,
            newItem.last_unsaved_at,
        ]),
    };
    return immer_1.default(newItem, draft => {
        Object.entries(enhancements).forEach(([key, value]) => {
            if (typeof value === 'undefined')
                return;
            if (value === draft[key])
                return // if (typeof (draft as any)[key] !== 'undefined') return
                ;
            draft[key] = value;
        });
    });
}
exports.mergeEventPreservingEnhancement = mergeEventPreservingEnhancement;
function getGitHubEventSubItems(event) {
    const { actor, payload, id, type, created_at: createdAt, } = event;
    const { merged: mergedIds } = event;
    const { repo: _repo } = event;
    const { repos: _repos } = event;
    const comment = payload.comment;
    const { commits: _commits } = payload;
    const forkee = payload
        .forkee;
    const { member: _member } = payload;
    const { release } = payload;
    const { pages: _pages } = payload;
    const { pull_request: pullRequest, } = payload;
    const { issue } = payload;
    const { ref: branchOrTagRef } = payload;
    const branchOrTagName = shared_2.getNameFromRef(branchOrTagRef);
    const issueOrPullRequest = (issue || pullRequest);
    const issueOrPullRequestNumber = issueOrPullRequest
        ? issueOrPullRequest.number ||
            url_1.getIssueOrPullRequestNumberFromUrl(issueOrPullRequest.url)
        : undefined;
    const isRead = shared_2.isItemRead(event);
    const isSaved = shared_2.isItemSaved(event);
    const commits = (_commits || []).filter(Boolean);
    const repos = (_repos || [_repo]).filter(r => {
        if (!(r && r.name))
            return false;
        const or = shared_2.getOwnerAndRepo(r.name);
        return !!(or.owner && or.repo);
    });
    // ugly and super edge case workaround for repo not being returned on some commit events
    if (!repos.length && commits[0]) {
        const _repoFullName = url_1.getRepoFullNameFromUrl(commits[0].url);
        const { owner, repo: name } = shared_2.getOwnerAndRepo(_repoFullName);
        if (owner && name) {
            repos.push({
                id: '',
                fork: false,
                private: false,
                full_name: _repoFullName,
                owner: { login: name },
                name,
                url: url_1.getGitHubURLForRepo(owner, name),
                html_url: url_1.getGitHubURLForRepo(owner, name),
            });
        }
    }
    const users = [_member].filter(Boolean); // TODO
    const pages = (_pages || []).filter(Boolean);
    const repo = repos.length === 1 ? repos[0] : undefined;
    const commitShas = commits
        .filter(Boolean)
        .map((item) => item.sha);
    const pageShas = pages.filter(Boolean).map((item) => item.sha);
    const repoIds = repos.filter(Boolean).map((item) => item.id);
    const userIds = users.filter(Boolean).map((item) => item.id);
    const repoFullName = repo && url_1.getRepoFullNameFromObject(repo);
    const forkRepoFullName = url_1.getRepoFullNameFromObject(forkee);
    const isPush = type === 'PushEvent';
    const isForcePush = isPush && payload.forced;
    const isPrivate = shared_1.isEventPrivate(event);
    const canSee = !isPrivate;
    const isBot = shared_2.getItemIsBot('activity', event, {
        considerProfileBotsAsBots: false,
    });
    const isBotOrFakeBot = shared_2.getItemIsBot('activity', event, {
        considerProfileBotsAsBots: true,
    });
    // GitHub returns the wrong avatar_url for app bots on actor.avatar_url,
    // but the correct avatar on payload.abc.user.avatar_url,
    // so lets get it from there instead
    const botAvatarURL = isBot
        ? url_1.getGitHubAvatarURLFromPayload(payload, actor.id)
        : undefined;
    const avatarUrl = botAvatarURL || actor.avatar_url;
    return {
        actor,
        avatarUrl,
        branchOrTagName,
        canSee,
        comment,
        commitShas,
        commits,
        createdAt,
        forkRepoFullName,
        forkee,
        id,
        isBot,
        isBotOrFakeBot,
        isBranchMainEvent: isBranchMainEvent(event),
        isForcePush,
        isPrivate,
        isPush,
        isRead,
        isSaved,
        isTagMainEvent: isTagMainEvent(event),
        issueOrPullRequest,
        issueOrPullRequestNumber,
        mergedIds,
        pageShas,
        pages,
        release,
        repoFullName,
        repoIds,
        repos,
        userIds,
        users,
    };
}
exports.getGitHubEventSubItems = getGitHubEventSubItems;
function getEventWatchingOwner(event, { dashboardFromUsername }) {
    let owner;
    if (event.type === 'ForkEvent' || event.type === 'WatchEvent') {
        const repoFullName = url_1.getRepoFullNameFromObject(event.repo);
        const repoOwner = repoFullName && repoFullName.split('/')[0];
        owner =
            repoOwner === dashboardFromUsername
                ? repoOwner
                : event.actor && event.actor.login;
    }
    else if (event.type === 'WatchEvent:OneUserMultipleRepos') {
        const repoFullName = url_1.getRepoFullNameFromObject(event.repos[0]);
        const repoOwner = repoFullName && repoFullName.split('/')[0];
        owner =
            repoOwner === dashboardFromUsername
                ? repoOwner
                : event.actor && event.actor.login;
    }
    else if (event.type === 'MemberEvent') {
        owner = event.payload.member && event.payload.member.login;
    }
    else {
        const repoFullName = url_1.getRepoFullNameFromObject(event.repo);
        owner = repoFullName && repoFullName.split('/')[0];
    }
    return owner || undefined;
}
exports.getEventWatchingOwner = getEventWatchingOwner;
//# sourceMappingURL=events.js.map